import { Redirect, Route, Switch } from 'wouter';
import { SessionProvider, useSession } from '@/auth/SessionContext';
import { AppShell } from '@/components/AppShell';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { ServiceProgressPage } from '@/pages/ServiceProgressPage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import { HealthReportsPage } from '@/pages/HealthReportsPage';
import { FinancePage } from '@/pages/FinancePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import type { ReactNode } from 'react';

/**
 * App 入口（Issue #198）。
 *
 * 路由策略：
 *   - /login 公共，已登录则重定向到 /。
 *   - 所有 /* 受保护路由统一包裹 AppShell；未登录跳 /login。
 *   - 不在前端做任何角色/权限分支：客户端只能访问 /me/* 接口，权限由后端裁决。
 */
export default function App() {
  return (
    <SessionProvider>
      <Switch>
        <Route path="/login">
          <RedirectIfAuthed>
            <LoginPage />
          </RedirectIfAuthed>
        </Route>
        <Route path="/progress">
          <RequireAuth>
            <AppShell>
              <ServiceProgressPage />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route path="/documents">
          <RequireAuth>
            <AppShell>
              <DocumentsPage />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route path="/reports">
          <RequireAuth>
            <AppShell>
              <HealthReportsPage />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route path="/finance">
          <RequireAuth>
            <AppShell>
              <FinancePage />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route path="/">
          <RequireAuth>
            <AppShell>
              <HomePage />
            </AppShell>
          </RequireAuth>
        </Route>
        <Route>
          <RequireAuth>
            <AppShell>
              <NotFoundPage />
            </AppShell>
          </RequireAuth>
        </Route>
      </Switch>
    </SessionProvider>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isInitializing } = useSession();
  if (isInitializing) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        会话初始化中…
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }
  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { isAuthenticated, isInitializing } = useSession();
  if (isInitializing) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        会话初始化中…
      </div>
    );
  }
  if (isAuthenticated) {
    return <Redirect to="/" />;
  }
  return <>{children}</>;
}
